import Spinner from 'common/components/Spinner'
import {detectDeviceType} from 'common/helpers/deviceType'
import {trackOnboardingEvents} from 'common/helpers/onboardingEventTracking'
import {
  ONBOARDING_PATH_AFTER_SAMPLE_DELETE,
  deleteAllSampleData,
  markOnboardingSampleDataCleared,
  resetOnboardingAfterSampleDataDelete,
} from 'common/helpers/onboardingSampleData'
import {SampleDataLiveCounts} from 'common/types/onboardingSampleData'
import {AlertTriangle, Info, ShieldCheck, Trash2, X} from 'lucide-react'
import {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import {RootState} from 'setup'

type Props = {
  show: boolean
  counts: SampleDataLiveCounts
  onHide: () => void
  onDeleted: () => void
}

const DeleteSampleDataModal: FC<Props> = ({show, onHide, onDeleted}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const urddId = useSelector<RootState>((state) => state.userReducer.urddId) as string | null
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!show) {
      setDeleting(false)
    }
  }, [show])

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await Promise.all([
        deleteAllSampleData(dispatch),
        // The CLICK. The confirmed clear (`data_clear/sample_data_clear`) is
        // written by node-api after the delete succeeds — never from here, so a
        // failed delete leaves a click row and no confirmed row. Both events go
        // as ONE ordered batch: the funnel writer reads arrival order as the
        // completion order (ONBOARDING_BULK_EVENTS spec §2).
        trackOnboardingEvents(dispatch, urddId, [
          {event_name: 'data_clear', substep: 'sample_data_clear_clicked'},
          {
            event_name: 'onboarding',
            substep: 'onboarding_started',
            metadata: {device_type: detectDeviceType()},
          },
        ]),
      ])
      await resetOnboardingAfterSampleDataDelete(dispatch)
      toast.success(intl.formatMessage({id: 'SAMPLE_DATA.DELETE.SUCCESS'}))
      onDeleted()
      onHide()
      markOnboardingSampleDataCleared()
      window.location.assign(ONBOARDING_PATH_AFTER_SAMPLE_DELETE)
    } catch (err: unknown) {
      const message =
        (err as {message?: string})?.message ??
        intl.formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'})
      toast.error(message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop={deleting ? 'static' : true}
      aria-labelledby='sample-data-delete-modal-title'
      backdropClassName='sample-data-delete-modal-backdrop'
      className='sample-data-delete-modal'
      contentClassName='sample-data-delete-modal__content'
      dialogClassName='sample-data-delete-modal__dialog'
    >
      <div className='sample-data-delete-modal__header'>
        <div className='sample-data-delete-modal__title-row'>
          <AlertTriangle size={30} className='sample-data-delete-modal__warning-icon' aria-hidden />
          <h2 id='sample-data-delete-modal-title' className='sample-data-delete-modal__title'>
            {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.TITLE'})}
          </h2>
        </div>
        <button
          type='button'
          className='sample-data-delete-modal__close'
          onClick={onHide}
          disabled={deleting}
          aria-label={intl.formatMessage({id: 'GENERAL.CLOSE'})}
        >
          <X size={22} aria-hidden />
        </button>
      </div>

      <div className='sample-data-delete-modal__body'>
        <p className='sample-data-delete-modal__intro'>
          {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.INTRO'})}
        </p>
        <p className='sample-data-delete-modal__intro'>
          {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.INTRO_2'})}
        </p>

        <div className='sample-data-delete-modal__box sample-data-delete-modal__box--warning'>
          <span className='sample-data-delete-modal__box-icon' aria-hidden>
            <Trash2 size={20} strokeWidth={2} />
          </span>
          <div>
            <p className='sample-data-delete-modal__box-title'>
              {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.WHAT_REMOVED_TITLE'})}
            </p>
            <p className='sample-data-delete-modal__box-text'>
              {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.WHAT_REMOVED_BODY'})}
            </p>
          </div>
        </div>

        <div className='sample-data-delete-modal__box sample-data-delete-modal__box--info'>
          <span className='sample-data-delete-modal__box-icon' aria-hidden>
            <Info size={20} strokeWidth={2} />
          </span>
          <div>
            <p className='sample-data-delete-modal__box-title'>
              {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.IMPORTANT_TITLE'})}
            </p>
            <p className='sample-data-delete-modal__box-text'>
              {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.IMPORTANT_BODY'})}
            </p>
          </div>
        </div>

        <p className='sample-data-delete-modal__footnote'>
          <ShieldCheck size={16} strokeWidth={2} aria-hidden />
          {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.CONTINUE_NOTE'})}
        </p>
      </div>

      <div className='sample-data-delete-modal__footer'>
        <button
          type='button'
          className='sample-data-delete-modal__btn sample-data-delete-modal__btn--cancel'
          onClick={onHide}
          disabled={deleting}
        >
          {intl.formatMessage({id: 'GENERAL.CANCEL'})}
        </button>
        <button
          type='button'
          className='sample-data-delete-modal__btn sample-data-delete-modal__btn--confirm'
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting ? (
            <>
              <Spinner size='sm' className='me-2' />
              {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.CLEARING'})}
            </>
          ) : (
            <>
              <Trash2 size={18} aria-hidden />
              {intl.formatMessage({id: 'SAMPLE_DATA.MODAL.CONFIRM_DELETE'})}
            </>
          )}
        </button>
      </div>
    </Modal>
  )
}

export {DeleteSampleDataModal}
